// Shared API helper for talking to the Hiilt gateway (user-service auth + manasa-service).
// Every manasa-service response is an ApiResponse envelope ({success, payload, error}).

const properties = require("../properties.json");

const TOKEN_KEY = "manasa_student_token";
const USER_KEY = "manasa_student_user";

export function getToken() {
  return window.localStorage.getItem(TOKEN_KEY);
}

export function getCurrentUser() {
  const raw = window.localStorage.getItem(USER_KEY);
  return raw ? JSON.parse(raw) : null;
}

export function isLoggedIn() {
  return !!getToken();
}

export function saveSession(token, user) {
  window.localStorage.setItem(TOKEN_KEY, token);
  window.localStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function clearSession() {
  window.localStorage.removeItem(TOKEN_KEY);
  window.localStorage.removeItem(USER_KEY);
}

function buildQuery(query) {
  if (!query) return "";
  const parts = Object.entries(query)
    .filter(([, v]) => v !== null && v !== undefined && v !== "")
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
  return parts.length ? `?${parts.join("&")}` : "";
}

/** Calls manasa-service through the gateway. `path` is relative to properties.api_path. */
export async function apiFetch(path, { method = "GET", body, query, auth = false } = {}) {
  const url = properties.api_path + path + buildQuery(query);
  const headers = { Accept: "application/json" };
  if (auth) {
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }
  if (body !== undefined) headers["Content-Type"] = "application/json";

  const res = await fetch(url, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (res.status === 204) return null;

  const json = await res.json();
  if (!json.success) {
    throw new Error((json.error && json.error.message) || "Something went wrong. Please try again.");
  }
  return json.payload;
}

/** Resolves which teacher the current hostname belongs to. Accepts an explicit `host` for
 *  local testing (?host=example.com) -- otherwise uses the real browser hostname. */
export function currentHost() {
  const params = new URLSearchParams(window.location.search);
  return params.get("host") || window.location.hostname;
}

export function resolveTenant() {
  return apiFetch("/lookup/tenant-by-host", { query: { host: currentHost() } });
}

/** Governorates/states are manasa-service's own (seeded independently of Hiilt's lookup_service
 *  -- see the extraction plan's Phase C), so these go through the normal apiFetch/api_path like
 *  everything else -- no separate gateway route to swap to. */
export function listGovernorates() {
  return apiFetch("/lookup/governorates");
}

export function listStates(governorateId) {
  return apiFetch("/lookup/states", { query: { governorateId } });
}

/** Student self-registration -- public, no auth required. */
export function registerStudent(payload) {
  return apiFetch("/students/register", { method: "POST", body: payload });
}

/** Public course catalog for a teacher -- browsable with no account. */
export function listCourseCatalog(teacherUserId) {
  return apiFetch("/courses/catalog", { query: { teacherUserId } });
}

/** Full curriculum for one course. Works with or without a logged-in session: an anonymous or
 *  non-enrolled visitor gets non-preview items locked (contentUrl withheld), same as the
 *  Flutter app's course detail screen. */
export function getCourse(id) {
  return apiFetch(`/courses/${id}`, { auth: true });
}

/** STUDENT-only: request activation/enrollment in the period a course belongs to -- pending
 *  the teacher's approval (see StudentController#createPeriodOrder). */
export function requestSubscription({ periodId, branchId }) {
  return apiFetch("/students/period-orders", { method: "POST", auth: true, body: { periodId, branchId } });
}

/** STUDENT-only: records the last item opened in this course (and, for VIDEO/AUDIO, how far into
 *  it) so re-entering the course resumes where they left off -- see CourseController#saveProgress.
 *  positionSeconds is optional (omit/null for non-media items). Silently ignored if not logged in
 *  as a student (no auth token) since apiFetch would 401/403 and the caller doesn't need to react. */
export function saveCourseProgress(courseId, itemId, positionSeconds) {
  if (!isLoggedIn()) return Promise.resolve();
  return apiFetch(`/courses/${courseId}/progress`, {
    method: "PUT",
    auth: true,
    body: { itemId, positionSeconds: positionSeconds ?? null },
  }).catch(() => {});
}

/** Student/teacher login (user-service, not gateway-routed under /api/manasa). */
export async function login(email, password) {
  const res = await fetch(properties.auth_path + "/login", {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ email, password }),
  });
  const json = await res.json();
  if (!res.ok || !json.success) {
    throw new Error((json.error && json.error.message) || "Incorrect login details.");
  }
  return json.payload; // { token, userId, email, fullName, role }
}
